/**
 * Mobile BottomSheet — rounded top corners, drag-down to close, focus trap.
 *
 * Sharp edges:
 *   - Restores focus to opener on close.
 *   - Pointer drag below an 80px threshold dismisses; otherwise snaps back.
 *   - ESC closes for keyboard users.
 *   - `aria-modal="true"` + `role="dialog"`.
 */
import {
  useEffect,
  useId,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';

import { trapFocus } from './focusTrap';

export interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  /** Maximum height as a percentage of the viewport. Defaults to 88. */
  maxHeightVh?: number;
  ariaLabel?: string;
}

const DRAG_DISMISS_THRESHOLD = 80; // px

export default function BottomSheet({
  open,
  onClose,
  title,
  children,
  maxHeightVh = 88,
  ariaLabel,
}: BottomSheetProps): JSX.Element | null {
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const dragStartY = useRef<number | null>(null);
  const [dragOffset, setDragOffset] = useState(0);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const t = window.setTimeout(() => cardRef.current?.focus(), 0);

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'Tab' && cardRef.current) {
        trapFocus(e, cardRef.current);
      }
    };
    document.addEventListener('keydown', onKey);

    return () => {
      window.clearTimeout(t);
      document.removeEventListener('keydown', onKey);
      previouslyFocused.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>): void => {
    dragStartY.current = e.clientY;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>): void => {
    if (dragStartY.current === null) return;
    const dy = e.clientY - dragStartY.current;
    setDragOffset(Math.max(0, dy));
  };

  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>): void => {
    if (dragStartY.current === null) return;
    const dy = e.clientY - dragStartY.current;
    dragStartY.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
    if (dy > DRAG_DISMISS_THRESHOLD) {
      setDragOffset(0);
      onClose();
    } else {
      setDragOffset(0);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(36, 26, 16, 0.45)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        touchAction: 'none',
      }}
    >
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : ariaLabel}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 600,
          background: 'var(--bg-surface)',
          borderTopLeftRadius: 'var(--radius-2xl)',
          borderTopRightRadius: 'var(--radius-2xl)',
          boxShadow: 'var(--shadow-sheet)',
          maxHeight: `${maxHeightVh}vh`,
          display: 'flex',
          flexDirection: 'column',
          transform: `translateY(${dragOffset}px)`,
          transition: dragOffset === 0 ? 'transform 200ms var(--ease-out)' : 'none',
          outline: 'none',
          touchAction: 'none',
        }}
      >
        <div
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          aria-hidden="true"
          style={{
            padding: '10px 0 6px',
            display: 'grid',
            placeItems: 'center',
            cursor: 'grab',
            touchAction: 'none',
          }}
        >
          <div
            style={{
              width: 40,
              height: 4,
              borderRadius: 2,
              background: 'var(--sand-300)',
            }}
          />
        </div>
        {title && (
          <div
            id={titleId}
            style={{
              padding: '4px 22px 14px',
              font: '600 17px var(--font-cjk-display)',
              color: 'var(--fg-1)',
            }}
          >
            {title}
          </div>
        )}
        <div style={{ overflowY: 'auto', padding: '0 20px 24px', flex: 1, overscrollBehavior: 'contain', touchAction: 'pan-y' }}>
          {children}
        </div>
      </div>
    </div>
  );
}
